import React, { useEffect, useRef, useState } from 'react';
import {
    DateInput,
    DateTimeInput,
    Datagrid,
    Filter,
    FunctionField,
    List,
    NumberInput,
    Pagination,
    SelectInput,
    TextField,
    TextInput,
    useInput,
    useMutation,
    useNotify,
    useRecordContext,
    useRefresh,
    useUnselectAll,
} from 'react-admin';
import { useForm, useFormState } from 'react-final-form';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import BlockIcon from '@material-ui/icons/Block';
import callPostRequest from '../api/callPostRequest';
import FreeLookupDomainActions from './FreeLookupDomainActions';
import { validateMaxNotBelowMin, validateNonNegativeInteger } from './numericRange';

/**
 * Free Lookup Administration (ASB Administration, ADMIN only).
 * Modelled after ../ticketadmin/TicketAdmin.tsx as instructed.
 * See anna-docs/FREE_LOOKUP_ADMINISTRATION_SPEC.md for the full feature spec.
 */

const FIELD_CHOICES = [
    { id: 'all', name: 'All fields' },
    { id: 'email', name: 'Email' },
    { id: 'name', name: 'Name' },
    { id: 'organization', name: 'Organization' },
    { id: 'validTo', name: 'Valid To/ Activity' },
    { id: 'lastLogin', name: 'Last Login' },
    { id: 'searches', name: 'Searches' },
    { id: 'exports', name: 'Exports' },
];

// Row filter on the user's status. "Blocked" includes users blocked via a blocked domain;
// "Blocked via domain" is the subset blocked only through the domain list. Combines (AND) with the
// field filter/search; it never hides or changes the other filter inputs.
// Server side: FreeLookupAdminService.statusCondition.
const STATUS_CHOICES = [
    { id: 'all', name: 'All' },
    { id: 'active', name: 'Active' },
    { id: 'blocked', name: 'Blocked' },
    { id: 'blockedDomain', name: 'Blocked via domain' },
    { id: 'unsubscribed', name: 'Unsubscribed' },
];

const DATE_RANGES = [
    { id: 'today', name: 'Today' },
    { id: 'week', name: 'Week' },
    { id: 'month', name: 'Month' },
    { id: 'year', name: 'Year' },
    { id: 'all', name: 'All Time' },
];

const DATE_RANGE_HELP_TEXT =
    'Only changes the counting window for the Searches and Exports columns below - ' +
    'it does not hide or filter out any users. Pick "All Time" to count each user\'s ' +
    'searches/exports since they registered.';

// Custom react-admin filter Input for the Date Range toggle buttons (Today/Week/Month/Year/All
// Time). Only narrows the Searches/Exports aggregation window server-side - does not hide rows.
// The caption + tooltip make that scope explicit, since "Date Range" could otherwise be read as a
// row filter (e.g. "only show users active this week").
const DateRangeInput: React.FC<any> = (props) => {
    const { input } = useInput(props);

    return (
        <div>
            <Tooltip title={DATE_RANGE_HELP_TEXT}>
                <Typography variant="caption" color="textSecondary" component="div">
                    Searches/Exports counted for
                </Typography>
            </Tooltip>
            <ToggleButtonGroup
                value={input.value || 'month'}
                exclusive
                size="small"
                onChange={(_event, newValue) => {
                    if (newValue) {
                        input.onChange(newValue);
                    }
                }}
            >
                {DATE_RANGES.map(range => (
                    <ToggleButton key={range.id} value={range.id}>
                        {range.name}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </div>
    );
};

// Search Bar widget: a From/To date range when "Valid To/ Activity" is the selected Filter-by field, a
// From/To datetime range for "Last Login", a plain text field otherwise (incl. "All Fields").
// Mirrors the formatted string/bounds the backend matches against (see
// FreeLookupAdminService.buildWhereClause/addRangeCondition: valid_to/last_login compared as
// 'YYYY-MM-DD'/'YYYY-MM-DD HH:mm') - the parse/format pair below keeps the Last Login values in
// that exact shape, with no Date-object/timezone round-trip; DateInput's native "YYYY-MM-DD"
// value already matches the Valid To/ Activity format as-is.
const parseLocalDateTime = (value: string) => value ? value.replace('T', ' ') : value;
const formatLocalDateTime = (value: string) => value ? value.replace(' ', 'T') : value;

const RANGE_FIELD_SOURCES = ['search', 'searchFrom', 'searchTo'];

const SearchValueInput: React.FC<any> = props => {
    const form = useForm();
    const { values } = useFormState();
    const searchField = values.searchField;
    const previousFieldRef = useRef(searchField);

    // Switching the Filter-by field makes any leftover search value(s) meaningless for the new
    // widget (e.g. free text left behind while a date range is now shown, or vice versa) - clear
    // all three possible sources on change.
    useEffect(() => {
        if (previousFieldRef.current !== searchField) {
            previousFieldRef.current = searchField;
            RANGE_FIELD_SOURCES.forEach(source => form.change(source, undefined));
        }
    }, [searchField, form]);

    // source/label from the <Filter> usage site only apply to the plain-text case below - the
    // range pickers use their own fixed sources/labels (searchFrom/searchTo, From/To) instead.
    const { source, label, ...rest } = props;

    switch (searchField) {
        case 'validTo':
            return (
                <div style={{ display: 'flex', gap: 8 }}>
                    <DateInput label="From" source="searchFrom" {...rest} />
                    <DateInput label="To" source="searchTo" {...rest} />
                </div>
            );
        case 'lastLogin':
            return (
                <div style={{ display: 'flex', gap: 8 }}>
                    <DateTimeInput label="From" source="searchFrom" parse={parseLocalDateTime} format={formatLocalDateTime} {...rest} />
                    <DateTimeInput label="To" source="searchTo" parse={parseLocalDateTime} format={formatLocalDateTime} {...rest} />
                </div>
            );
        case 'searches':
        case 'exports':
            // Min/Max (inclusive, both optional) replace the old exact-number text match. Reuses the
            // searchFrom/searchTo sources, which are cleared above whenever the field changes.
            return (
                <div style={{ display: 'flex', gap: 8 }}>
                    <NumberInput label="Min" source="searchFrom" min={0} step={1}
                                 validate={validateNonNegativeInteger} {...rest} />
                    <NumberInput label="Max" source="searchTo" min={0} step={1}
                                 validate={[validateNonNegativeInteger, validateMaxNotBelowMin]} {...rest} />
                </div>
            );
        default:
            return <TextInput {...props} />;
    }
};

const FreeLookupAdminFilter: React.FC<any> = props => (
    <Filter {...props}>
        <SelectInput label="Filter by" source="searchField" choices={FIELD_CHOICES} alwaysOn />
        <SearchValueInput label="Search users" source="search" alwaysOn />
        {/* "All" is the neutral value, so no extra empty option; "Clear filters" sets '' which shows as "All". */}
        <SelectInput label="Status" source="status" choices={STATUS_CHOICES} allowEmpty={false}
                     format={(value: string) => value || 'all'} alwaysOn />
        <DateRangeInput label="" source="dateRange" alwaysOn />
    </Filter>
);

// Rendered via react-admin's <FunctionField render={...}/> (see Datagrid below) rather than as
// standalone components, matching the convention used elsewhere (Tickets.tsx, ChallengeFields.tsx) -
// a <Datagrid> child must accept the `source`/`label`/etc. field props react-admin injects, which a
// plain no-props component can't do without widening its type away from what it actually uses.
const renderName = (record: any) => {
    const name = [record.firstName, record.lastName].filter(Boolean).join(' ');
    return <span>{name || '—'}</span>;
};

const renderOrganization = (record: any) => <span>{record.organization || '(unverified)'}</span>;

// Per the reference mockup: a blocked user's "Valid To/ Activity" cell shows "Blocked" in red instead of the
// date. domainBlocked (derived from isin.anna_isin_blocked_domain, see FreeLookupAdminService) is
// shown the same way but with its own label - it's a different cause (the user row itself was
// never individually blocked) and admins need to tell the two apart.
const renderValidTo = (record: any) => {
    if (record.userBlocked) {
        return <span style={{ color: '#c62828', fontWeight: 'bold' }}>Blocked</span>;
    }
    if (record.domainBlocked) {
        return <span style={{ color: '#c62828', fontWeight: 'bold' }}>Blocked via domain</span>;
    }
    // Unsubscribed users stay in anna_isin_user (only is_unsubscribed/unsubscribed_date are set).
    if (record.unsubscribed) {
        return <span style={{ color: '#ef6c00', fontWeight: 'bold' }}>Unsubscribed</span>;
    }
    return <span>{record.validTo || '-'}</span>;
};

const renderLastLogin = (record: any) => {
    if (!record.lastLogin) return <span>-</span>;
    return <span>{new Date(record.lastLogin).toLocaleString()}</span>;
};

// Per-row Blocked Status toggle - clicking blocks, clicking again unblocks the same user.
// Copied from ../ticketadmin/TicketAdmin.tsx's `Toggle` component as instructed.
const BlockedToggle: React.FC<{ source: string; label?: string }> = ({ source }) => {
    const record = useRecordContext();
    const [mutate, { loading }] = useMutation();
    const notify = useNotify();
    const refresh = useRefresh();

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!record) return;

        const updatedRecord = {
            ...record,
            [source]: event.target.checked,
        };

        await mutate({
            type: 'update',
            resource: 'freelookupadmin',
            payload: { id: record.id, data: updatedRecord, previousData: record },
        }, {
            onSuccess: () => {
                notify(event.target.checked ? 'User blocked' : 'User unblocked', 'info');
                refresh();
            },
            onFailure: (error) => {
                notify(`Error: could not save changes. ${error.message}`, 'warning');
            },
        });
    };

    // domainBlocked users aren't individually blocked (userBlocked stays false/untouched - see
    // FreeLookupAdminService.blockDomain()), so the per-user toggle doesn't apply to them: there's
    // nothing on this row to flip, the block lives on the domain list instead. Leave the cell
    // empty rather than showing a toggle that wouldn't do what it looks like it does.
    if (!record || record.domainBlocked) return null;

    return (
        <Checkbox
            checked={!!record[source]}
            onChange={handleChange}
            disabled={loading}
        />
    );
};

// Bulk "Block user" action for the rows selected via the grid's row-selection checkboxes.
// Block-only, by design - unblocking always happens per-row via BlockedToggle (see spec §4).
const FreeLookupBulkActionButtons: React.FC<any> = ({ selectedIds = [], resource }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const unselectAll = useUnselectAll(resource);
    const [loading, setLoading] = useState(false);

    const handleBlock = async () => {
        setLoading(true);
        try {
            await callPostRequest('/anna/api/v1/freelookupadmin/block', { emailIds: selectedIds.join(',') });
            notify(`${selectedIds.length} user(s) blocked`, 'info');
            unselectAll();
            refresh();
        } catch (error) {
            notify(`Error: ${error.message}`, 'warning');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            variant="contained"
            color="primary"
            startIcon={<BlockIcon />}
            disabled={loading || selectedIds.length === 0}
            onClick={handleBlock}
        >
            Block user
        </Button>
    );
};

export const FreeLookupAdmin: React.FC = props => (
    <List
        {...props}
        title="Free Lookup Administration"
        actions={<FreeLookupDomainActions />}
        bulkActionButtons={<FreeLookupBulkActionButtons />}
        filters={<FreeLookupAdminFilter />}
        filterDefaultValues={{ searchField: 'all', dateRange: 'month', status: 'all' }}
        perPage={25}
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
    >
        <Datagrid>
            <TextField source="emailId" label="Email" />
            <FunctionField source="name" label="Name" render={renderName} />
            <FunctionField source="organization" label="Organization" render={renderOrganization} />
            <FunctionField source="validTo" label="Valid To/ Activity" render={renderValidTo} />
            <FunctionField source="lastLogin" label="Last Login" render={renderLastLogin} />
            <TextField source="searches" label="Searches" />
            <TextField source="exports" label="Exported" />
            <BlockedToggle source="userBlocked" label="Blocked" />
        </Datagrid>
    </List>
);
